// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// map.ts — the floor as data, and the three geometric questions asked of it: can I stand there, can he
// see me, can he hear me.
//
// EVERY ANSWER IS AN INTEGER. IEEE-754 pins +, -, *, / and sqrt to the bit on every conforming engine
// and says NOTHING about sin, cos, atan2, pow or exp, which genuinely differ in the last place between
// JavaScript engines. A shared building, a re-simulated ghost link and a 3,600-run balance sweep all
// rest on (seed, mode, action log) determining the night completely, so a single transcendental in
// here is not a wobble — it is a different building. tests/source-hygiene.test.ts greps for them.
//
// THE ARC TEST IS EXACT AND HAS NO TRIGONOMETRY IN IT. A cell lies within a half-angle of a facing iff
// the dot product against the facing dominates the cross product:
//
//     |theta| <= 45deg   <=>   dot >= |cross|          (because cos t >= |sin t| <=> |t| <= 45)
//     |theta| <= atan(2) <=>   2 * dot >= |cross|      (63.43deg, the widened SWEEP arc)
//
// Both sides scale by |facing| * |delta|, so the test is scale-invariant and works unchanged for the
// diagonal facings whose vectors are not unit length. The wide arc is atan(2) = 63.43deg rather than a
// rounded 60deg on purpose: a rational boundary is a boundary two independent implementations can
// agree on exactly, and a zero-tolerance audit cannot be built on an irrational one.

import { COLS, CELLS, ROWS, SOUND_CLOSED_DOOR, SOUND_STEP, Floor } from './tuning';

/**
 * The wide arc's boundary, as an exact rational: |theta| <= atan(WIDE_TAN).
 * 2 gives 63.43deg. Deliberately not a rounded 60deg — see the header.
 */
const WIDE_TAN = 2;

export const idx = (x: number, y: number): number => y * COLS + x;
export const xOf = (c: number): number => c % COLS;
export const yOf = (c: number): number => (c / COLS) | 0;
export const inBounds = (x: number, y: number): boolean => x >= 0 && x < COLS && y >= 0 && y < ROWS;
export const chebyshev = (a: number, b: number): number =>
  Math.max(Math.abs(xOf(a) - xOf(b)), Math.abs(yOf(a) - yOf(b)));

/** The eight facings, indexed by octant. y grows downward, so octant 2 (north) is dy = -1. */
export const DIRS: readonly (readonly [number, number])[] = [
  [1, 0], // 0 E
  [1, -1], // 1 NE
  [0, -1], // 2 N
  [-1, -1], // 3 NW
  [-1, 0], // 4 W
  [-1, 1], // 5 SW
  [0, 1], // 6 S
  [1, 1], // 7 SE
];
/** The four orthogonal facings, as octants. Movement is 4-adjacent; only facing is 8-way. */
export const ORTHO: readonly number[] = [0, 2, 4, 6];

export enum DoorState {
  Open = 0,
  Closed = 1,
  Locked = 2,
}

export interface Door {
  cell: number;
  state: DoorState;
  /** Set when SWEEP auto-locks it, so the band dropping can release exactly those doors again. */
  autoLocked: boolean;
}

/** The static floor: everything generation decides once and the simulation never rewrites. */
export interface Grid {
  /** 1 = solid. Solid cells are neither walkable nor transparent, ever. */
  wall: Uint8Array;
  floorT: Uint8Array;
  /** Room id per cell, or -1 for a wall or a door. Breakers and gloom are per-room. */
  room: Int8Array;
  /** Index into `doors`, or -1. */
  doorAt: Int16Array;
  /** 0 or 1 for the two paired duct mouths, else -1. */
  ductAt: Int8Array;
  roomCount: number;
}

export const makeGrid = (): Grid => ({
  wall: new Uint8Array(CELLS),
  floorT: new Uint8Array(CELLS),
  room: new Int8Array(CELLS).fill(-1),
  doorAt: new Int16Array(CELLS).fill(-1),
  ductAt: new Int8Array(CELLS).fill(-1),
  roomCount: 0,
});

/** Can a body occupy this cell? A door cell is walkable only while the door is open. */
export function canStep(g: Grid, doors: readonly Door[], c: number): boolean {
  if (c < 0 || c >= CELLS || g.wall[c]) return false;
  const d = g.doorAt[c];
  return d < 0 || doors[d].state === DoorState.Open;
}

/** Does this cell stop sight? A closed OR locked door does; an open one does not. */
export function isOpaque(g: Grid, doors: readonly Door[], c: number): boolean {
  if (g.wall[c]) return true;
  const d = g.doorAt[c];
  return d >= 0 && doors[d].state !== DoorState.Open;
}

/** The four orthogonal neighbours of a cell that exist on the board. */
export function neighbours(c: number, out: number[]): number[] {
  out.length = 0;
  const x = xOf(c);
  const y = yOf(c);
  if (x > 0) out.push(c - 1);
  if (x < COLS - 1) out.push(c + 1);
  if (y > 0) out.push(c - COLS);
  if (y < ROWS - 1) out.push(c + COLS);
  return out;
}

/**
 * Is `target` inside the wedge of half-angle `wide ? atan(2) : 45deg` centred on `facing` at `from`?
 * Pure angle — range and line of sight are separate questions, asked separately.
 */
export function inArc(from: number, facing: number, target: number, wide: boolean): boolean {
  const dx = xOf(target) - xOf(from);
  const dy = yOf(target) - yOf(from);
  if (dx === 0 && dy === 0) return true;
  const [fx, fy] = DIRS[facing & 7];
  const dot = dx * fx + dy * fy;
  if (dot <= 0) return false;
  const cross = Math.abs(dx * fy - dy * fx);
  return cross <= (wide ? WIDE_TAN : 1) * dot;
}

/**
 * The cells a supercover ray from `a` to `b` passes through, EXCLUDING both endpoints.
 *
 * "Supercover" means every cell the true segment touches, not just one per major-axis step — so a ray
 * that clips the corner between two walls is blocked, which is what a player reading a floor plan
 * expects. Written as an integer DDA. tests/helpers/audit.ts implements the SAME defined rule by a
 * different construction (parametric sampling and dedup) rather than a different rule: two spellings
 * of one rule catch typos and off-by-ones, which is real value, and pretending they are independent
 * geometries would be the exact tautology this whole audit exists to avoid. What IS independent over
 * there is the light mask, the posture factor and the door mask — the things the design review caught
 * the auditor consuming from the game instead of deriving.
 */
export function rayCells(a: number, b: number, out: number[]): number[] {
  out.length = 0;
  const x0 = xOf(a);
  const y0 = yOf(a);
  const x1 = xOf(b);
  const y1 = yOf(b);
  let dx = Math.abs(x1 - x0);
  let dy = Math.abs(y1 - y0);
  const sx = x0 < x1 ? 1 : -1;
  const sy = y0 < y1 ? 1 : -1;
  let x = x0;
  let y = y0;
  // Doubling both deltas lets the diagonal tie (err === 0) be detected exactly in integers; on a tie
  // the ray passes through the corner, so BOTH corner cells are covered.
  dx *= 2;
  dy *= 2;
  let err = dx - dy;
  const guard = COLS + ROWS + 4;
  for (let i = 0; i < guard; i++) {
    if (x === x1 && y === y1) break;
    if (err === 0) {
      // Exact corner crossing: cover both orthogonal neighbours before stepping diagonally.
      if (!(x + sx === x1 && y === y1)) out.push(idx(x + sx, y));
      if (!(x === x1 && y + sy === y1)) out.push(idx(x, y + sy));
      x += sx;
      y += sy;
      err = dx - dy;
    } else if (err > 0) {
      x += sx;
      err -= dy;
    } else {
      y += sy;
      err += dx;
    }
    if (x === x1 && y === y1) break;
    out.push(idx(x, y));
  }
  return out;
}

const RAY_BUF: number[] = [];

/** Is there an unobstructed line from `a` to `b`? Endpoints are never their own blockers. */
export function hasLos(g: Grid, doors: readonly Door[], a: number, b: number): boolean {
  if (a === b) return true;
  const cells = rayCells(a, b, RAY_BUF);
  for (let i = 0; i < cells.length; i++) if (isOpaque(g, doors, cells[i])) return false;
  return true;
}

/**
 * Every cell a din of loudness `loud` reaches from `at`, as a distance map (-1 = out of earshot).
 *
 * Dijkstra rather than plain BFS, because a closed door costs more than a step. Walls block
 * absolutely: sound goes around corners and through doorways, which is the entire reason a thrown slug
 * can empty a wing you cannot see into.
 */
export function soundField(
  g: Grid,
  doors: readonly Door[],
  at: number,
  loud: number,
  extraAtSource = 0,
): Int16Array {
  const dist = new Int16Array(CELLS).fill(-1);
  if (loud <= 0) return dist;
  const start = extraAtSource;
  if (start > loud) return dist;
  dist[at] = start;
  // Costs are 1 or 3 and the budget is tiny, so a bucket queue is exact, allocation-free per step and
  // has no comparator to get wrong.
  const buckets: number[][] = [];
  const push = (c: number, d: number) => {
    (buckets[d] ||= []).push(c);
  };
  push(at, start);
  const nb: number[] = [];
  for (let d = start; d <= loud; d++) {
    const q = buckets[d];
    if (!q) continue;
    for (let qi = 0; qi < q.length; qi++) {
      const c = q[qi];
      if (dist[c] !== d) continue;
      neighbours(c, nb);
      for (let i = 0; i < nb.length; i++) {
        const n = nb[i];
        if (g.wall[n]) continue;
        const door = g.doorAt[n];
        const step = door >= 0 && doors[door].state !== DoorState.Open ? SOUND_CLOSED_DOOR : SOUND_STEP;
        const nd = d + step;
        if (nd > loud) continue;
        if (dist[n] === -1 || nd < dist[n]) {
          dist[n] = nd;
          push(n, nd);
        }
      }
    }
  }
  return dist;
}

/** Walking distance from `from` to every reachable cell, ignoring sound. -1 = unreachable. */
export function walkField(g: Grid, doors: readonly Door[], from: number): Int16Array {
  const dist = new Int16Array(CELLS).fill(-1);
  if (!canStep(g, doors, from)) return dist;
  dist[from] = 0;
  const q = [from];
  const nb: number[] = [];
  for (let h = 0; h < q.length; h++) {
    const c = q[h];
    neighbours(c, nb);
    for (let i = 0; i < nb.length; i++) {
      const n = nb[i];
      if (dist[n] !== -1 || !canStep(g, doors, n)) continue;
      dist[n] = dist[c] + 1;
      q.push(n);
    }
  }
  return dist;
}

/**
 * One step of the shortest path from `from` toward `to`, or -1.
 *
 * The tiebreak is the lowest cell index and it is load-bearing rather than incidental: two minders
 * reading the same mark on the same turn must choose the same route on every device, or a shared
 * building stops being shared.
 */
export function stepToward(g: Grid, doors: readonly Door[], from: number, to: number): number {
  if (from === to) return -1;
  const field = walkField(g, doors, to);
  if (field[from] <= 0) return -1;
  const nb: number[] = [];
  neighbours(from, nb);
  let best = -1;
  let bestD = 1 << 20;
  for (let i = 0; i < nb.length; i++) {
    const n = nb[i];
    if (!canStep(g, doors, n) || field[n] === -1) continue;
    if (field[n] < bestD || (field[n] === bestD && n < best)) {
      bestD = field[n];
      best = n;
    }
  }
  return bestD < field[from] ? best : -1;
}

/** The octant that best describes the step from `a` to `b`, for facing a body after it moves. */
export function facingOf(a: number, b: number): number {
  const dx = Math.sign(xOf(b) - xOf(a));
  const dy = Math.sign(yOf(b) - yOf(a));
  for (let o = 0; o < 8; o++) if (DIRS[o][0] === dx && DIRS[o][1] === dy) return o;
  return 0;
}

export const floorName = (f: number): string =>
  f === Floor.Grating ? 'grating' : f === Floor.Tile ? 'tile' : 'carpet';
